import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { Button, Mascot, Modal } from '@xdocs/ui';
import { useFtueContext } from './FtueProvider.js';
import { FTUE_STEPS } from './ftueSteps.js';
import { useFtueT } from './useFtueT.js';
import styles from './FtueTour.module.css';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 8;

/**
 * Tracks the current step's `targetSelector` in the live DOM, re-measuring
 * on resize/scroll so the spotlight ring follows a target that moves (e.g.
 * a responsive layout reflow mid-tour). Returns `null` whenever the
 * selector is absent or resolves to nothing — the F9 edge case ("coach-marks
 * must target real, present elements") degrades to "no ring", never a
 * misplaced one.
 */
function useSpotlightRect(selector: string | undefined): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!selector) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: resetting the DOM-measured spotlight rect when the step has no target is itself the DOM-measurement effect's job, not derivable during render.
      setRect(null);
      return;
    }

    function measure(): void {
      const el = selector ? document.querySelector(selector) : null;
      if (!el) {
        setRect(null);
        return;
      }
      const box = el.getBoundingClientRect();
      setRect({
        top: box.top - SPOTLIGHT_PADDING,
        left: box.left - SPOTLIGHT_PADDING,
        width: box.width + SPOTLIGHT_PADDING * 2,
        height: box.height + SPOTLIGHT_PADDING * 2,
      });
    }

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [selector]);

  return rect;
}

function SpotlightRing({ rect }: { rect: Rect }): ReactElement | null {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      aria-hidden="true"
      className={styles['spotlight']}
      style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
    />,
    document.body,
  );
}

/**
 * The FTUE guided tour overlay (F9). Reads/drives the single shared
 * `useFtue()` instance via `<FtueProvider>` context — mount ONE `<FtueTour
 * />` inside that provider (see `./WIRING.md`).
 *
 * Built on `@xdocs/ui`'s `<Modal>` for the dialog chrome (focus-trap,
 * Esc-to-dismiss, restore-focus-on-close, inert background) — same
 * deliberate simplification `NotificationsBell` already documents (no
 * dedicated anchored coach-mark primitive in `@xdocs/ui` yet). Esc maps to
 * `skip()` (F9: "skip at any step must be one click + obvious").
 * `prefers-reduced-motion` is honored by `<Mascot>` itself (no animate prop
 * needed here — floaty animation auto-disables) and by this module's CSS
 * (spotlight/dot transitions off under reduced motion).
 */
export function FtueTour(): ReactElement | null {
  const { t } = useFtueT();
  const { isOpen, stepIndex, stepCount, isFirstStep, isLastStep, next, back, skip, finish, isPersisting } =
    useFtueContext();
  const step = FTUE_STEPS[stepIndex];
  const rect = useSpotlightRect(step?.targetSelector);

  if (!isOpen || !step) return null;

  return (
    <>
      {rect && <SpotlightRing rect={rect} />}
      <Modal
        open={isOpen}
        onClose={skip}
        title={t(`ftue:${step.titleKey}`)}
        size="md"
        footer={
          <div className={styles['footer']}>
            <Button variant="ghost" size="lg" onClick={skip}>
              {t('ftue:nav.skip')}
            </Button>
            <div className={styles['footerNav']}>
              {!isFirstStep && (
                <Button variant="secondary" size="lg" onClick={back}>
                  {t('ftue:nav.back')}
                </Button>
              )}
              {isLastStep ? (
                <Button variant="primary" size="lg" state={isPersisting ? 'loading' : 'idle'} onClick={finish}>
                  {t('ftue:nav.finish')}
                </Button>
              ) : (
                <Button variant="primary" size="lg" onClick={next}>
                  {isFirstStep ? t('ftue:nav.start') : t('ftue:nav.next')}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className={styles['body']}>
          <div className={styles['progress']} role="status" aria-label={t('ftue:nav.progress', { current: stepIndex + 1, total: stepCount })}>
            {FTUE_STEPS.map((s, i) => (
              <span key={s.id} className={i === stepIndex ? `${styles['dot']} ${styles['dotActive']}` : styles['dot']} />
            ))}
          </div>
          <Mascot pose={step.pose} size={120} alt="" animate={!isLastStep} />
          <p className={styles['eyebrow']}>{t(`ftue:${step.eyebrowKey}`)}</p>
          <p className={styles['stepBody']}>{t(`ftue:${step.bodyKey}`)}</p>
        </div>
      </Modal>
    </>
  );
}
